import React from 'react';
import I18n from 'i18n-js';
import { Location } from 'history';

import { Sale } from '../../../schemas/rql';
import constants from '../../shared/constants';
import { getDateAndTimeFromSeconds } from '../../../lib/date_utils';

import SanitizedRender from '@reverbdotcom/commons/src/components/sanitized_render';

const { ENDED, NOT_STARTED, TIME_EXPIRED } = constants.SALE_BUYER_INELIGIBILITY_REASONS;

const SELLER = 'seller';
const CUSTOM = 'custom';

interface IProps {
  sale: Sale;
  description?: string;
  location: Location;
}

function SaleEndDatetime({ sale }: { sale: Sale }) {
  const [placeholder, setPlaceholder] = React.useState('…');

  React.useEffect(() => {
    /*
    * Currently RRE is not aware of browser timezone so it's unsafe to SSR timestamps.
    * This is the most prominent place where we do that, so as a quick fix let's useEffect
    * so the server renders a placeholder that gets replaced by the actual timestamp client-side
    */
    setPlaceholder(null);
  }, []);

  if (sale.buyerIneligibilityReason === ENDED ||
    sale.buyerIneligibilityReason === TIME_EXPIRED) {
    const dateTime = placeholder || getDateAndTimeFromSeconds(sale.endsAt.seconds);

    return I18n.t('discovery.saleInformation.saleEnded', { dateTime });
  }

  if (sale.buyerIneligibilityReason === NOT_STARTED) {
    const dateTime = placeholder || getDateAndTimeFromSeconds(sale.startsAt.seconds);
    return I18n.t('discovery.saleInformation.saleBegins', { dateTime });
  }

  if (sale.state === 'LIVE' && !sale.buyerIneligibilityReason) {
    const dateTime = placeholder || getDateAndTimeFromSeconds(sale.endsAt.seconds);
    return I18n.t('discovery.saleInformation.saleEnds', { dateTime });
  }

  return null;
}

export class SaleInformation extends React.Component<IProps, null> {
  get shopHref() {
    return `/shop/${this.props.sale.shopSlug}`;
  }

  get discountText() {
    return I18n.t(
      'discovery.saleInformation.discount_without_code',
      { discountValue: this.props.sale.discountValue },
    );
  }

  renderSafeDescription() {
    if (!this.props.description) { return null; }
    if (this.props.sale.saleType === CUSTOM) { return null; }

    return (
      <SanitizedRender
        className="sale-information__description"
        html={this.props.description}
      />
    );
  }

  renderDangerousDescription() {
    if (!this.props.description) { return null; }
    if (this.props.sale.saleType !== CUSTOM) { return null; }

    return (
      <div
        className="sale-information__landing"
        dangerouslySetInnerHTML={{ __html: this.props.description }}
      >
      </div>
    );
  }

  renderShopInfo() {
    if (this.props.sale.saleType !== SELLER) { return false; }

    return (
      <div className="sale-information__fineprint__shop-info">
        {I18n.t('discovery.saleInformation.createdBy')}
        <b><a href={this.shopHref}>{this.props.sale.shopName}</a></b>
      </div>
    );
  }

  render() {
    if (!this.props.sale) { return null; }

    return (
      <aside className="">
        <div className="sale-information__inner">
          <div className="sale-information__details">
            <div className="sale-information__discount">
              <SanitizedRender html={this.discountText} />
            </div>
            <div className="sale-information__fineprint__date-info">
              <SaleEndDatetime sale={this.props.sale} />
            </div>
            {this.renderSafeDescription()}
            {this.renderShopInfo()}
          </div>
        </div>
        {this.renderDangerousDescription()}
      </aside>
    );
  }
}

export default SaleInformation;
